/**
 * useCrossAppSync — read/write Attuned-tier cross-app sync preferences.
 *
 * Per V2 §2: All-Access (Attuned) members choose what syncs across the three
 * communities (photo, bio, prompts, voice, matches) vs stays separate.
 * Free for the Attuned tier. Disabled (and visually locked) for everyone else.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type CrossAppSyncPrefs = {
  sync_photos: boolean;
  sync_bio: boolean;
  sync_prompts: boolean;
  sync_voice: boolean;
  sync_matches: boolean;
};

export const DEFAULT_PREFS: CrossAppSyncPrefs = {
  sync_photos: false,
  sync_bio: false,
  sync_prompts: false,
  sync_voice: false,
  sync_matches: false,
};

export const useCrossAppSync = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<CrossAppSyncPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!user) {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("cross_app_sync_prefs")
        .select("sync_photos, sync_bio, sync_prompts, sync_voice, sync_matches")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active) return;
      if (data) setPrefs(data as CrossAppSyncPrefs);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const update = useCallback(
    async (patch: Partial<CrossAppSyncPrefs>): Promise<{ error?: string }> => {
      if (!user) return { error: "Not signed in" };
      const next = { ...prefs, ...patch };
      setPrefs(next); // optimistic
      const { error } = await supabase
        .from("cross_app_sync_prefs")
        .upsert(
          { user_id: user.id, ...next, updated_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );
      if (error) return { error: error.message };
      return {};
    },
    [user, prefs],
  );

  return { prefs, loading, update };
};
